// useDeviceFacility — "which facility is THIS wall standing in, and may this
// account open it?"
//
// ─────────────────────────────────────────────────────────────────────────────
// THE INVERSION, ONE MORE TIME
// ─────────────────────────────────────────────────────────────────────────────
// GeofenceGate VERIFIES ("is this device at facility X?", X is an input) and
// FAILS OPEN — a facility with no fence configured still loads the board,
// asserted deliberately at GeofenceGate.test.js:7 so a fresh tenant is not
// locked out of its own board. That is correct for a phone carrying an account
// that already knows where it works.
//
// This hook RESOLVES (X is the output) and FAILS CLOSED. No fix, a denied
// permission, no configured fences, a fix that matches nothing, or a position
// inside two overlapping radii all resolve to NOTHING and the caller renders
// FacilityPicker. A wall silently showing another hospital's OR board is a
// tenancy and patient-safety failure; a wall asking which hospital it is in is
// an inconvenience.
//
// ─────────────────────────────────────────────────────────────────────────────
// CONFIRM, NEVER SWITCH
// ─────────────────────────────────────────────────────────────────────────────
// AlertProvider derives its facility from the signed-in account
// (AlertContext.js:41) and cannot be overridden. A panel rendering any OTHER
// facility loses emergencies and break requests SILENTLY — no error, the board
// just looks calm. So the geofence may only CONFIRM the account's facility:
//
//   • it never writes `activeFacilityId`;
//   • a resolution the account cannot open is a PICKER case, not a switch.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHAT IT DOES NOT DO
// ─────────────────────────────────────────────────────────────────────────────
// No `watchPosition`. A fixed wall display resolves ONCE at mount plus whatever
// the human asks for with `retry()`. Continuous watching burns tablet battery
// and buys nothing on a device that does not move.
//
// The pure part lives in domain/facilityResolver.js and is already tested; this
// hook is the lifecycle around it — permission probe, one fix, persistence.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  facilityDistances,
  resolveFacilityByPosition,
  usableFence,
} from "../domain/facilityResolver";

export const DEVICE_FACILITY_KEY = "atc.panel.facility.v1";

// Verbatim from GeofenceGate.js:127. High accuracy on a mains-powered TV with
// no GPS buys nothing and costs a longer wait; the 15 s timeout is what turns a
// kiosk that can never answer a permission prompt into "no fix → picker →
// stored choice" instead of an indefinite spinner.
export const GEO_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 15000,
  maximumAge: 300000,
};

// Reasons the picker may be showing, or the reason a rendered facility is only
// "set on this device". Every one of them is a fail-closed outcome except
// `locating`, which is the transient before the first fix arrives.
export const REASONS = {
  LOCATING: "locating",
  AMBIGUOUS: "ambiguous",
  DENIED: "denied",
  UNAVAILABLE: "unavailable",
  UNCONFIGURED: "unconfigured",
  OUT_OF_RANGE: "out-of-range",
  MISMATCH: "mismatch",
  ACCOUNT_SCOPE: "account-scope",
};

// localStorage is unavailable in private mode and throws on access, not just on
// write — same guard as BoardPage.js:137.
export function readStoredFacility() {
  try {
    const raw = localStorage.getItem(DEVICE_FACILITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const id = parsed && parsed.facilityId;
    if (typeof id !== "string" || id.trim() === "") return null;
    return { facilityId: id, setAt: parsed.setAt || null, source: parsed.source || "picked" };
  } catch {
    return null; // private mode, or somebody hand-edited the value
  }
}

function writeStoredFacility(entry) {
  try {
    localStorage.setItem(DEVICE_FACILITY_KEY, JSON.stringify(entry));
  } catch {
    /* private mode — the choice simply does not survive the reboot */
  }
}

// The picker list when there is no usable fix. `facilityDistances` deliberately
// returns [] in that case rather than a column of nulls, so the rows are built
// here WITHOUT distances — a badge reading "—" invites the reader to believe a
// distance was measured and came back empty.
function plainCandidates(facilities) {
  if (!Array.isArray(facilities)) return [];
  const seen = new Set();
  const out = [];
  for (const f of facilities) {
    const id = f && (f.id || f.facilityId);
    if (typeof id !== "string" || id.trim() === "" || seen.has(id)) continue;
    seen.add(id);
    out.push({
      facilityId: id,
      label: (typeof f.name === "string" && f.name) || (typeof f.label === "string" && f.label) || id,
      distanceM: null,
      radiusM: null,
      inRange: false,
    });
  }
  return out;
}

/**
 * @param {object}   opts
 * @param {Array}    opts.facilities        every facility doc the account can see
 * @param {string?}  opts.accountFacilityId `user.activeFacilityId` — the ONLY
 *                                          facility AlertProvider will scope to
 * @param {string[]} opts.allowedFacilityIds `user.facilityIds`
 * @param {boolean}  opts.isAdmin           admins are exempt from the facilityIds
 *                                          check (not from the account check)
 * @returns {{ facilityId, verification, reason, candidates, retry, choose,
 *             distanceM, checking }}
 *   `facilityId === null` is the caller's instruction to render FacilityPicker.
 *   `verification` is "verified" (geofence confirmed it, `distanceM` set) or
 *   "device" (a stored choice nothing has confirmed) or null.
 */
export function useDeviceFacility({
  facilities,
  accountFacilityId = null,
  allowedFacilityIds = null,
  isAdmin = false,
} = {}) {
  const [stored, setStored] = useState(readStoredFacility);
  // The id a human picked ON THIS SCREEN, this session. It is not persisted:
  // its only job is to distinguish "a stored value nobody has looked at since
  // the display moved" from "a human just told the picker, having been shown
  // that the geofence disagrees". Without it the mismatch case is a dead end —
  // the picker comes up, the human picks, and the same disagreement puts the
  // picker straight back up forever.
  const [confirmedId, setConfirmedId] = useState(null);
  const [geo, setGeo] = useState({ status: "checking", coords: null });
  // Bumping this re-runs the whole lifecycle. It is the ONLY thing that does —
  // a fixed display asks once and then only when a human asks again.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let dead = false;

    // `"geolocation" in navigator` is not enough: a wall browser can expose the
    // key with nothing behind it, and calling through it throws inside an
    // effect, which takes the whole panel down instead of showing a picker.
    const sensor = typeof navigator === "undefined" ? null : navigator.geolocation;
    if (!sensor || typeof sensor.getCurrentPosition !== "function") {
      setGeo({ status: "unavailable", coords: null });
      return undefined;
    }
    setGeo({ status: "checking", coords: null });

    const ask = () => {
      sensor.getCurrentPosition(
        (pos) => {
          if (dead) return;
          setGeo({
            status: "fixed",
            coords: { lat: pos?.coords?.latitude, lng: pos?.coords?.longitude },
          });
        },
        (err) => {
          if (dead) return;
          // 1 = PERMISSION_DENIED (GeofenceGate.js:120-125). Everything else —
          // position unavailable, timeout — is "no sensor / no fix". Both route
          // to the picker here, with different chips: that IS the inversion.
          setGeo({ status: err && err.code === 1 ? "denied" : "unavailable", coords: null });
        },
        GEO_OPTIONS
      );
    };

    // Probe permissions FIRST. A kiosk whose permission is permanently denied
    // would otherwise burn the full 15 s timeout on every single boot before
    // reaching the picker. navigator.permissions is not universal on TV
    // browsers, so a missing or throwing API is treated as "prompt" and we ask.
    const perms = typeof navigator !== "undefined" ? navigator.permissions : null;
    let probe = null;
    if (perms && typeof perms.query === "function") {
      try {
        probe = perms.query({ name: "geolocation" });
      } catch {
        probe = null;
      }
    }
    if (probe && typeof probe.then === "function") {
      probe.then(
        (status) => {
          if (dead) return;
          if (status && status.state === "denied") {
            setGeo({ status: "denied", coords: null });
            return;
          }
          ask();
        },
        () => {
          if (!dead) ask();
        }
      );
    } else {
      ask();
    }

    // The dead flag, not an AbortController: getCurrentPosition has no cancel,
    // so the callbacks must refuse to touch state after unmount themselves
    // (GeofenceGate.js:108, :129-131).
    return () => {
      dead = true;
    };
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const choose = useCallback((facilityId) => {
    if (typeof facilityId !== "string" || facilityId.trim() === "") return;
    const entry = { facilityId, setAt: Date.now(), source: "picked" };
    writeStoredFacility(entry);
    setStored(entry);
    setConfirmedId(facilityId);
  }, []);

  const coords = geo.status === "fixed" ? geo.coords : null;

  const resolution = useMemo(
    () => (coords ? resolveFacilityByPosition(coords.lat, coords.lng, facilities) : null),
    [coords, facilities]
  );

  const allCandidates = useMemo(
    () =>
      coords
        ? facilityDistances(coords.lat, coords.lng, facilities)
        : plainCandidates(facilities),
    [coords, facilities]
  );

  // "No geofence configured" is a property of the TENANT, not of the fix: it is
  // true whether or not the device ever got a position, and it is the only
  // honest chip when there is nothing anywhere to match against.
  const anyUsableFence = useMemo(
    () => (Array.isArray(facilities) ? facilities.some((f) => !!usableFence(f)) : false),
    [facilities]
  );

  // A facility is openable iff this account would actually receive its alerts
  // and pass its queries:
  //   • it is the account's own facility — AlertProvider is hard-scoped to
  //     `activeFacilityId` and cannot be overridden, so ANY other facility
  //     renders a board that silently drops emergencies; and
  //   • it is inside `facilityIds` (admins exempt, per the §5.4 addition) —
  //     the resolver knows geography, not permissions, and a board that renders
  //     then fails every query is worse than a picker.
  const canOpen = useCallback(
    (facilityId) => {
      if (!facilityId) return false;
      const allowed = Array.isArray(allowedFacilityIds) ? allowedFacilityIds.filter(Boolean) : [];
      if (!isAdmin && allowed.length > 0 && !allowed.includes(facilityId)) return false;
      if (accountFacilityId && facilityId !== accountFacilityId) return false;
      return true;
    },
    [accountFacilityId, allowedFacilityIds, isAdmin]
  );

  // Offer ONLY what this account can actually open.
  //
  // Listing a facility the scope gate will then refuse walks the operator into
  // a dead end: they pick it, the picker comes straight back, and nothing on
  // screen tells them the list itself was the problem. Observed exactly that
  // way on a live board before this filter existed.
  //
  // The information is not lost — when the geofence RESOLVES to a facility this
  // account cannot open, the reason chip still says so. What changes is that
  // the list stops offering a choice that cannot succeed. With an account
  // scoped to one facility this reduces to a single confirm button, which is
  // the honest shape of that situation.
  const candidates = useMemo(
    () => allCandidates.filter((c) => canOpen(c.facilityId)),
    [allCandidates, canOpen]
  );

  // ── The §5.4 persistence table, in order ────────────────────────────────
  let facilityId = null;
  let verification = null;
  let reason = null;
  let distanceM = null;

  if (geo.status === "checking") {
    // Row 1 — stored present, geofence still resolving: render it IMMEDIATELY
    // and say it is unverified. A wall that shows nothing for 15 s every boot
    // gets unplugged.
    reason = REASONS.LOCATING;
    if (stored) {
      facilityId = stored.facilityId;
      verification = "device";
    }
  } else if (resolution && resolution.status === "resolved") {
    const disagrees = !!stored && stored.facilityId !== resolution.facilityId;
    if (disagrees) {
      // Row 3 — the two authorities disagree. A stored choice plus a geofence
      // naming a different campus is either a moved device or a spoofed
      // position, and BOTH readings mean a human should confirm. Never switch
      // silently, never clear the stored value.
      reason = REASONS.MISMATCH;
      if (confirmedId === stored.facilityId) {
        // …and this is that human, on this screen, having read the chip that
        // says the location disagrees. Their answer stands — but it is a
        // CONFIRMATION, not a verification, so the rail still reads
        // "set on this device", never "location verified".
        facilityId = stored.facilityId;
        verification = "device";
      }
    } else {
      // Rows 2 and 5 — confirmed, or adopted because nothing was stored.
      facilityId = resolution.facilityId;
      verification = "verified";
      distanceM = resolution.distanceM;
    }
  } else {
    // Rows 4 and 6 — every fail-closed outcome. A stored choice SURVIVES all of
    // them ("keep stored, never clear"); with nothing stored there is nothing
    // to render and the caller shows the picker.
    if (resolution && resolution.status === "ambiguous") reason = REASONS.AMBIGUOUS;
    else if (geo.status === "denied") reason = REASONS.DENIED;
    else if (geo.status === "unavailable") reason = REASONS.UNAVAILABLE;
    else if (!anyUsableFence) reason = REASONS.UNCONFIGURED;
    else reason = REASONS.OUT_OF_RANGE;

    if (stored) {
      facilityId = stored.facilityId;
      verification = "device";
    }
  }

  // Row 7 — the account-scope gate, applied to whatever survived above,
  // including a stored choice. Geography does not grant permission.
  if (facilityId && !canOpen(facilityId)) {
    facilityId = null;
    verification = null;
    distanceM = null;
    reason = REASONS.ACCOUNT_SCOPE;
  }

  // Adopting an unambiguous resolution persists it as `source: "geofence"` so
  // the next boot renders instantly. Keyed on the id alone: once written,
  // `stored` matches and this settles rather than looping.
  const adoptId = verification === "verified" && !stored ? facilityId : null;
  useEffect(() => {
    if (!adoptId) return;
    const entry = { facilityId: adoptId, setAt: Date.now(), source: "geofence" };
    writeStoredFacility(entry);
    setStored(entry);
  }, [adoptId]);

  return {
    facilityId,
    verification,
    reason,
    candidates,
    distanceM,
    checking: geo.status === "checking",
    retry,
    choose,
  };
}

export default useDeviceFacility;
